import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/lib/theme';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import {
  Globe,
  Cloud,
  Server,
  HardDrive,
  Gamepad2,
  Headphones,
  Key,
  Shield,
  Lock,
  ChevronDown,
  Menu,
  X,
  Sun,
  Moon,
  Search,
  User,
} from 'lucide-react';

const navItems = [
  {
    key: 'domains',
    label: 'Domains',
    icon: Globe,
    items: [
      { label: 'Domain Check', href: 'https://deinserverhost.de/store/domainchecker.php', target: '_blank' },
      { label: 'Register Domain', href: 'https://deinserverhost.de/store/cart.php?a=add&domain=register', target: '_blank' },
      { label: 'Transfer Domain', href: 'https://deinserverhost.de/store/cart.php?a=add&domain=transfer', target: '_blank' },
    ],
  },
  {
    key: 'webhosting',
    label: 'Web-Hosting',
    icon: Cloud,
    items: [
      { label: 'SSD Hosting', href: '/webhosting/ssd' },
      { label: 'NVMe Hosting', href: '/webhosting/nvme' },
      { label: 'Nextcloud', href: '/webhosting/nextcloud' },
    ],
  },
  {
    key: 'vps',
    label: 'VPS',
    icon: Server,
    items: [
      { label: 'Storage VPS', href: '/vps/storage' },
      { label: 'KVM Ryzen', href: '/vps/kvm-ryzen' },
      { label: 'KVM VPS', href: '/vps/kvm' },
      { label: 'Windows VPS', href: '/vps/windows' },
    ],
  },
  {
    key: 'rootserver',
    label: 'Root',
    icon: HardDrive,
    items: [
      { label: 'Linux KVM', href: '/rootserver/linux' },
      { label: 'Windows KVM', href: '/rootserver/windows' },
    ],
  },
  {
    key: 'dedicated',
    label: 'Dedicated',
    icon: Server,
    items: [
      { label: 'Standard', href: '/dedicated/standard' },
      { label: 'Premium', href: '/dedicated/premium' },
      { label: 'Sale Servers', href: 'https://deinserverhost.de/dedicated-sonderposten', target: '_blank', rel: 'noopener noreferrer' },
    ],
  },
  {
    key: 'teamspeak',
    label: 'Voice',
    icon: Headphones,
    href: '/teamspeak',
  },
  {
    key: 'gameservers',
    label: 'Games',
    icon: Gamepad2,
    href: '/gameservers',
  },
  {
    key: 'more',
    label: 'More',
    icon: Key,
    items: [
      { label: 'Licenses', href: '/licenses' },
      { label: 'VPN', href: '/vpn' },
      { label: 'SSL', href: '/ssl' },
    ],
  },
];

export const Navbar = () => {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, [location]);

  // Escape key closes dropdowns and mobile menu
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setActiveDropdown(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      {/* Skip Link for Accessibility */}
      <a href="#main-content" className="skip-link" data-testid="skip-link">
        {t('a11y.skipToContent')}
      </a>

      <header
          className={cn(
              'fixed left-0 right-0 z-50 transition-all duration-300',
              isScrolled
                  ? 'bg-background/95 backdrop-blur-md border-b border-border shadow-lg'
                  : 'bg-transparent'
          )}
          style={{ top: '36px' }}
          role="banner"
      >
        <nav
          className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14"
          aria-label="Main navigation"
        >
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 shrink-0 group"
            data-testid="logo-link"
            aria-label="DeinServerHost - Home"
            title="DeinServerHost - Home"
          >
            <img
              src="/black.svg"
              alt="DeinServerHost"
              title="DeinServerHost"
              className="h-8 w-auto block dark:hidden"
            />
            <img
              src="/white.svg"
              alt="DeinServerHost"
              title="DeinServerHost"
              className="h-8 w-auto hidden dark:block"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center">
            {navItems.map((item) =>
              item.items ? (
                <div
                  key={item.key}
                  className="relative"
                  onMouseEnter={() => setActiveDropdown(item.key)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button
                    className={cn(
                      'flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors rounded-md',
                      activeDropdown === item.key
                        ? 'text-primary bg-primary/10'
                        : 'text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5'
                    )}
                    aria-expanded={activeDropdown === item.key}
                    aria-haspopup="true"
                    data-testid={`nav-${item.key}`}
                  >
                    {item.label}
                    <ChevronDown
                      className={cn(
                        'w-3 h-3 transition-transform duration-200',
                        activeDropdown === item.key && 'rotate-180'
                      )}
                    />
                  </button>

                  <AnimatePresence>
                    {activeDropdown === item.key && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-1 w-48 bg-card border border-border rounded-lg shadow-xl overflow-hidden"
                      >
                        {item.items.map((subItem) => (
                          <Link
                            key={subItem.href}
                            to={subItem.href}
                            target={subItem.target}
                            rel={subItem.rel}
                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-primary/10 transition-colors"
                            data-testid={`nav-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={item.key}
                  to={item.href}
                  className={cn(
                    'px-3 py-2 text-sm font-medium transition-colors rounded-md',
                    location.pathname === item.href
                      ? 'text-primary bg-primary/10'
                      : 'text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5'
                  )}
                  aria-current={location.pathname === item.href ? 'page' : undefined}
                  data-testid={`nav-${item.key}`}
                >
                  {item.label}
                </Link>
              )
            )}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-1">
            {/* Product Finder */}
            <Link
              to="/product-finder"
              className="hidden md:flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5 rounded-md transition-colors"
              aria-label="Product Finder"
              title="Product Finder"
              data-testid="nav-product-finder"
            >
              <Search className="w-4 h-4" />
              <span className="hidden xl:inline">Finder</span>
            </Link>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              aria-label={t('a11y.toggleTheme')}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
            </button>

            {/* Language Switcher */}
              {/*
              <LanguageSwitcher />
            */}
            {/* Customer Area */}
            <a
              href="https://deinserverhost.de/store/clientarea.php"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:flex items-center gap-1.5 ml-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-semibold hover:bg-primary/90 transition-colors"
              aria-label="Customer Area Login"
              title="Customer Area Login"
              data-testid="customer-area-btn"
            >
              <User className="w-4 h-4" />
              <span className="hidden md:inline">Login</span>
            </a>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-md text-foreground hover:bg-black/5 dark:hover:bg-white/5 ml-1 mr-3"
              aria-label={mobileOpen ? t('a11y.closeMenu') : t('a11y.openMenu')}
              aria-expanded={mobileOpen}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-card border-t border-border"
            >
              <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
                {navItems.map((item) =>
                  item.items ? (
                    <div key={item.key} className="space-y-1">
                      <button
                        onClick={() => setActiveDropdown(activeDropdown === item.key ? null : item.key)}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-md text-foreground hover:bg-black/5 dark:hover:bg-white/5"
                        aria-expanded={activeDropdown === item.key}
                      >
                        <span className="flex items-center gap-2">
                          <item.icon className="w-4 h-4 text-primary" />
                          {item.label}
                        </span>
                        <ChevronDown
                          className={cn(
                            'w-4 h-4 transition-transform',
                            activeDropdown === item.key && 'rotate-180'
                          )}
                        />
                      </button>
                      <AnimatePresence>
                        {activeDropdown === item.key && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="pl-6 space-y-1"
                          >
                            {item.items.map((subItem) => (
                              <Link
                                key={subItem.href}
                                to={subItem.href}
                                className="block px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-md hover:bg-black/5 dark:hover:bg-white/5"
                                onClick={() => setMobileOpen(false)}
                              >
                                {subItem.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      key={item.key}
                      to={item.href}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-foreground hover:bg-black/5 dark:hover:bg-white/5"
                      onClick={() => setMobileOpen(false)}
                    >
                      <item.icon className="w-4 h-4 text-primary" />
                      {item.label}
                    </Link>
                  )
                )}

                <div className="pt-3 mt-3 border-t border-border space-y-1">
                  <Link
                    to="/product-finder"
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-foreground hover:bg-black/5 dark:hover:bg-white/5"
                    onClick={() => setMobileOpen(false)}
                  >
                    <Search className="w-4 h-4 text-primary" />
                    Product Finder
                  </Link>
                  <a
                    href="https://deinserverhost.de/store/clientarea.php"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 rounded-md bg-primary text-primary-foreground"
                  >
                    <User className="w-4 h-4" />
                    Customer Area
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Navbar;
